import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import KpiCard from './KpiCard';
import CustomerHistoryModal from './CustomerHistoryModal';
import { getCustomerStatus } from '../services/dashboardApi';

function ActiveListModal({ customers, onClose, onOpenCustomer }) {
  const [q, setQ] = useState('');
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? customers.filter((c) => String(c.name || '').toLowerCase().includes(s)) : customers;
  }, [customers, q]);

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
    >
      <div
        role="dialog"
        aria-label="Active customers"
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--color-surface)', borderRadius: 'var(--radius-md)', padding: 'var(--space-5)', maxWidth: 460, width: '92%', maxHeight: '75vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 30px rgba(0,0,0,0.2)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
          <h3 style={{ margin: 0, fontSize: 'var(--font-size-md)' }}>{`Active Customers (${customers.length})`}</h3>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>✕</button>
        </div>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search customer…"
          aria-label="Search active customer"
          style={{ padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-3)' }}
        />
        <div style={{ overflowY: 'auto' }}>
          {shown.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)' }}>No customers in this list.</p>
          ) : (
            <ol style={{ margin: 0, paddingLeft: 24 }}>
              {shown.map((c) => (
                <li key={c.id} style={{ fontSize: 'var(--font-size-sm)', padding: '4px 0' }}>
                  <button
                    type="button"
                    onClick={() => onOpenCustomer(c)}
                    title="Open full dispatch history"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--color-accent)', font: 'inherit', textAlign: 'left', textDecoration: 'underline' }}
                  >
                    {c.name}
                  </button>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

function Clickable({ label, onActivate, enabled, children }) {
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={label}
      title={enabled ? 'Click to see the list' : undefined}
      onClick={enabled ? onActivate : undefined}
      onKeyDown={(e) => { if (enabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onActivate(); } }}
      style={{ display: 'flex', flex: '1 1 160px', cursor: enabled ? 'pointer' : 'default' }}
    >
      {children}
    </div>
  );
}

/** Dashboard cards: the user's own active / inactive customers (admins see everyone's). */
export default function CustomerStatusCards() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [failed, setFailed] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [historyCustomer, setHistoryCustomer] = useState(null);

  useEffect(() => {
    let alive = true;
    getCustomerStatus()
      .then((s) => { if (alive) setStatus(s); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, []);

  const show = (list) => {
    if (failed) return '—';
    return status ? list.length.toLocaleString() : '…';
  };
  const active = status ? status.active : [];
  const inactive = status ? status.inactive : [];

  return (
    <>
      <Clickable label="Active Customers" enabled={Boolean(status)} onActivate={() => navigate('/active-customers')}>
        <KpiCard label="Active Customers" value={show(active)} tone="success" />
      </Clickable>
      <Clickable
        label="Inactive Customers"
        enabled={Boolean(status) && inactive.length > 0}
        onActivate={() => navigate('/inactive-customers', { state: { customers: inactive, title: 'Inactive Customers' } })}
      >
        <KpiCard label="Inactive Customers" value={show(inactive)} tone={inactive.length > 0 ? 'warning' : 'success'} />
      </Clickable>
      {listOpen && (
        <ActiveListModal customers={active} onClose={() => setListOpen(false)} onOpenCustomer={(c) => setHistoryCustomer({ id: c.id, name: c.name })} />
      )}
      {historyCustomer && <CustomerHistoryModal customer={historyCustomer} onClose={() => setHistoryCustomer(null)} />}
    </>
  );
}
